import { createFileRoute } from '@tanstack/react-router';
import { LeaderboardTableUsers } from './-LeaderboardTableUsers';
import { LeaderboardTableCountries } from './-LeaderboardTableCountries';
import { LeaderboardTableClans } from './-LeaderboardTableClans';
import { LeaderboardQuery } from '~/util/queries/public/leaderboard';
import {
    LeaderboardCountryEntry,
    PaginatedLeaderboardResponse,
} from '~/util/types/leaderboard';
import { fromNumeric, getCountryName } from '~/util/countries';
import Paginator from '~/components/Paginator';

export const Route = createFileRoute('/leaderboard/')({
    component: LeaderboardData,
    loaderDeps: ({
        search: { search, list, type, page, country, clan, sort },
    }) => ({
        search,
        list,
        type,
        page,
        country,
        clan,
        sort,
    }),
    loader: async ({
        context: { queryClient },
        deps: { search, list, type, page, country, clan, sort },
    }) => {
        const data = await queryClient.fetchQuery(
            LeaderboardQuery({
                list,
                type,
                page,
                sort,
                nameFilter: search,
                countryFilter: country,
                clanFilter: clan?.id,
            }),
        );
        if (type === 'countries') {
            const countryFilteredData = (
                data.data as LeaderboardCountryEntry[]
            ).filter((entry) =>
                getCountryName(fromNumeric(entry.country))
                    .toLowerCase()
                    .includes(search?.toLowerCase() ?? ''),
            );

            const countryTotalPages = Math.ceil(
                (countryFilteredData ?? []).length / 20,
            );

            const normalizedPage =
                (page ?? 1) > countryTotalPages ? 1 : (page ?? 1);

            const countryPaginatedData = countryFilteredData.slice(
                (normalizedPage - 1) * 20,
                normalizedPage * 20,
            );

            return {
                ...data,
                data: countryPaginatedData,
                page: normalizedPage,
                per_page: 20,
                pages: countryTotalPages,
            };
        } else {
            return data as PaginatedLeaderboardResponse;
        }
    },
});

function LeaderboardData() {
    const type = Route.useSearch({ select: ({ type }) => type });
    const { page, pages } = Route.useLoaderData({
        select: ({ page, pages }) => ({
            page,
            pages,
        }),
    });
    const navigate = Route.useNavigate();

    return (
        <>
            {type === 'users' ? (
                <LeaderboardTableUsers />
            ) : type === 'countries' ? (
                <LeaderboardTableCountries />
            ) : (
                <LeaderboardTableClans />
            )}

            <Paginator
                currentPage={page}
                totalPages={pages}
                onPageChange={(page) =>
                    navigate({
                        search: (prev) => ({
                            ...prev,
                            page: page,
                        }),
                    })
                }
            />
        </>
    );
}
